import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiTrash2, FiUpload, FiEye, FiAlertCircle, FiCheck, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const ExcelColumnMapper = ({ onMappingChange, initialMapping }) => {
  // Initialize categories state
  const getInitialCategories = () => {
    if (initialMapping && Array.isArray(initialMapping) && initialMapping.length > 0) {
      return initialMapping;
    }
    // Default empty category
    return [{
      id: 1,
      name: 'assessments',
      displayName: 'Assessments',
      components: [{
        id: 1,
        name: 'component_1',
        displayName: 'Component 1',
        columnIndex: 0, // Column A (0-based indexing)
        maxMarks: 50,
        description: 'Assessment component'
      }]
    }];
  };

  const [categories, setCategories] = useState(() => getInitialCategories());
  const [showPreview, setShowPreview] = useState(false);
  const [sampleExcelData, setSampleExcelData] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [hasHeaderRow, setHasHeaderRow] = useState(false);
  const [canAutoGenerate, setCanAutoGenerate] = useState(false);
  const [previewComponents, setPreviewComponents] = useState([]);
  const [hasAutoPopulated, setHasAutoPopulated] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());

  // Update if initialMapping changes
  useEffect(() => {
    if (initialMapping && Array.isArray(initialMapping) && initialMapping.length > 0) {
      setCategories(initialMapping);
    }
  }, [initialMapping]);

  // Update parent when mapping changes (debounced to prevent excessive re-renders)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onMappingChange) {
        onMappingChange(categories);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [categories]); // Removed onMappingChange from dependencies

  const addCategory = () => {
    const newCategory = {
      id: Date.now(),
      name: `category_${categories.length + 1}`,
      displayName: `Category ${categories.length + 1}`,
      components: []
    };
    setCategories([...categories, newCategory]);
  };

  const removeCategory = (categoryId) => {
    setCategories(categories.filter(cat => cat.id !== categoryId));
  };

  const updateCategory = (categoryId, field, value) => {
    setCategories(prevCategories => prevCategories.map(cat => 
      cat.id === categoryId ? { ...cat, [field]: value } : cat
    ));
  };

  const addComponent = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    const newComponent = {
      id: Date.now(),
      name: `component_${category.components.length + 1}`,
      displayName: `Component ${category.components.length + 1}`,
      columnIndex: category.components.length, // Start from 0 (Column A)
      maxMarks: 50,
      description: 'Assessment component'
    };
    
    setCategories(categories.map(cat => 
      cat.id === categoryId 
        ? { ...cat, components: [...cat.components, newComponent] }
        : cat
    ));
  };

  const removeComponent = (categoryId, componentId) => {
    setCategories(categories.map(cat => 
      cat.id === categoryId 
        ? { ...cat, components: cat.components.filter(comp => comp.id !== componentId) }
        : cat
    ));
  };

  const updateComponent = (categoryId, componentId, field, value) => {
    setCategories(prevCategories => prevCategories.map(cat => 
      cat.id === categoryId 
        ? {
            ...cat,
            components: cat.components.map(comp => 
              comp.id === componentId ? { ...comp, [field]: value } : comp
            )
          }
        : cat
    ));
  };

  const toggleCategoryCollapse = (categoryId) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const detectAutoGenerationPossibility = (data) => {
    if (!data || data.length < 3) return false;
    
    // Look for "Assessment Total" or "Grand Total" in the data
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      if (row[0] && (row[0].includes('Assessment Total') || row[0].includes('Grand Total'))) {
        // Generate preview components
        generatePreviewComponents(data);
        return true;
      }
    }
    return false;
  };

  const generatePreviewComponents = (data) => {
    try {
      let headerRow = null;
      let assessmentTotalRow = null;
      let grandTotalRow = null;

      // Find the relevant rows
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        if (row[0] && row[0].includes('Assessment Total')) {
          assessmentTotalRow = row;
          headerRow = i > 0 ? data[i - 1] : null;
        } else if (row[0] && row[0].includes('Grand Total')) {
          grandTotalRow = row;
          if (!headerRow && i > 0) {
            headerRow = data[i - 1];
          }
        }
      }

      if (!headerRow || (!assessmentTotalRow && !grandTotalRow)) {
        return;
      }

      // Use Assessment Total if available, otherwise Grand Total (for maxMarks)
      const totalRow = assessmentTotalRow || grandTotalRow;
      
      // Generate preview components
      const components = [];
      for (let i = 1; i < headerRow.length && i < totalRow.length; i++) {
        const componentName = headerRow[i] ? headerRow[i].trim() : `Component ${i}`;
        const maxMarks = parseInt(totalRow[i]) || 50;
        
        if (componentName && maxMarks > 0) {
          components.push({
            name: componentName,
            column: String.fromCharCode(65 + i),
            columnIndex: i,
            maxMarks: maxMarks
          });
        }
      }

      setPreviewComponents(components);
    } catch (error) {
      console.error('Error generating preview:', error);
      setPreviewComponents([]);
    }
  };

  const generateComponentsFromCSV = () => {
    if (!sampleExcelData || sampleExcelData.length < 3) return;

    try {
      let headerRow = null;
      let assessmentTotalRow = null;
      let grandTotalRow = null;

      // Find the relevant rows
      for (let i = 0; i < sampleExcelData.length; i++) {
        const row = sampleExcelData[i];
        if (row[0] && row[0].includes('Assessment Total')) {
          assessmentTotalRow = row;
          headerRow = i > 0 ? sampleExcelData[i - 1] : null;
        } else if (row[0] && row[0].includes('Grand Total')) {
          grandTotalRow = row;
          if (!headerRow && i > 0) {
            headerRow = sampleExcelData[i - 1];
          }
        }
      }

      if (!headerRow || (!assessmentTotalRow && !grandTotalRow)) {
        setUploadMessage('Could not detect proper CSV structure for auto-generation');
        return;
      }

      // Use Assessment Total if available, otherwise Grand Total (for maxMarks)
      const totalRow = assessmentTotalRow || grandTotalRow;
      
      // Generate components
      const components = [];
      for (let i = 1; i < headerRow.length && i < totalRow.length; i++) {
        const componentName = headerRow[i] ? headerRow[i].trim() : `Component ${i}`;
        const maxMarks = parseInt(totalRow[i]) || 50;
        
        // Get both Assessment Total and Grand Total values for this column
        const assessmentTotal = assessmentTotalRow && assessmentTotalRow[i] ? parseInt(assessmentTotalRow[i]) || 0 : null;
        const grandTotal = grandTotalRow && grandTotalRow[i] ? parseInt(grandTotalRow[i]) || 0 : null;
        
        if (componentName && maxMarks > 0) {
          const sanitizedName = componentName.toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_]/g, '') || `component_${i}`;
          
          // Make component name unique if it's a duplicate
          const uniqueSanitizedName = sanitizedName + (components.some(c => c.name === sanitizedName) ? `_${i}` : '');
          const uniqueDisplayName = componentName + (components.some(c => c.displayName === componentName) ? ` (${String.fromCharCode(65 + i)})` : '');
          
          components.push({
            id: Date.now() + i * 1000, // Better unique ID generation
            name: uniqueSanitizedName,
            displayName: uniqueDisplayName,
            columnIndex: i, // Column index (0-based: A=0, B=1, C=2, etc.)
            maxMarks: maxMarks,
            assessmentTotal: assessmentTotal, // Store Assessment Total value
            grandTotal: grandTotal, // Store Grand Total value
            description: `${uniqueDisplayName} assessment`
          });
          
          console.log(`Component ${i}: "${componentName}" -> "${uniqueSanitizedName}", Column ${i}, Max: ${maxMarks}, Assessment: ${assessmentTotal}, Grand: ${grandTotal}`);
        }
      }

      // Create a new category with the generated components
      const newCategory = {
        id: Date.now(),
        name: 'assessments',
        displayName: 'Assessments (Auto-populated from CSV)',
        components: components
      };

      console.log('Generated components:', components);
      console.log('New category:', newCategory);
      
      // Force update by creating a completely new array
      setCategories(() => {
        const newCategories = [newCategory];
        console.log('Setting categories to:', newCategories);
        return newCategories;
      });
      setUploadMessage(`Successfully populated ${components.length} component fields from CSV! Review and edit as needed before saving.`);
      setCanAutoGenerate(false); // Hide the auto-generate section after success
      setPreviewComponents([]); // Clear preview
      setHasAutoPopulated(true); // Mark as auto-populated
      
    } catch (error) {
      console.error('Error generating components from CSV:', error);
      setUploadMessage('Error generating components from CSV structure');
    }
  };

  const handleSampleExcelUpload = (event) => {
    const file = event.target.files[0];
    setUploadMessage(''); // Clear previous messages
    setCanAutoGenerate(false);
    setPreviewComponents([]); // Clear preview
    if (file) {
      const fileType = file.name.split('.').pop().toLowerCase();
      
      if (fileType === 'csv') {
        // Parse CSV file
        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target.result;
          const rows = text.split('\n').filter(row => row.trim());
          const data = rows.map(row => {
            // Handle CSV with potential commas in values
            const values = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < row.length; i++) {
              const char = row[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim());
            return values;
          });
          
          // Store all data for auto-generation, but only show first few rows for preview
          setSampleExcelData(data);
          setUploadMessage(`Successfully loaded ${data.length} rows from CSV file. Showing first 3 rows.`);
          
          // Check if auto-generation is possible
          const canAutoGen = detectAutoGenerationPossibility(data);
          setCanAutoGenerate(canAutoGen);
        };
        reader.onerror = () => {
          setUploadMessage('Error reading file. Please try again.');
          setSampleExcelData(null);
        };
        reader.readAsText(file);
      } else {
        // For Excel files, show a message
        setUploadMessage('Excel file upload requires the xlsx library. Please save your Excel file as CSV format.');
        setSampleExcelData(null);
        // Show instructions in alert
        alert('Excel (.xlsx/.xls) file support requires additional setup.\n\nPlease either:\n1. Save your Excel file as CSV format (File → Save As → CSV)\n2. Or manually configure the column mapping below');
      }
    }
  };

  const generateYAML = () => {
    const learnerDataMap = {
      assessments: {}
    };

    categories.forEach(category => {
      learnerDataMap.assessments[category.name] = {
        category_name: category.displayName,
        components: {}
      };

      category.components.forEach(component => {
        learnerDataMap.assessments[category.name].components[component.name] = {
          column_index: component.columnIndex,
          max_marks: component.maxMarks,
          grand_total: component.grandTotal,
          description: component.description
        };
      });
    });

    return `learner_data_map:\n${formatYAML(learnerDataMap, 1)}`;
  };

  const formatYAML = (obj, indent = 0) => {
    const spaces = '  '.repeat(indent);
    let yaml = '';
    
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null) {
        yaml += `${spaces}${key}:\n${formatYAML(value, indent + 1)}`;
      } else {
        yaml += `${spaces}${key}: ${value}\n`;
      }
    }
    
    return yaml;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Excel Column Mapping</h3>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
          >
            <FiEye className="w-4 h-4 mr-1" />
            Preview YAML
          </button>
          <button
            type="button"
            onClick={addCategory}
            className="flex items-center px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200"
          >
            <FiPlus className="w-4 h-4 mr-1" />
            Add Category
          </button>
        </div>
      </div>

      {/* Sample Excel Upload */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Sample File Upload (Optional)</h4>
        <p className="text-sm text-gray-600 mb-3">
          Upload a CSV file (exported from Excel) to see column headers and preview data mapping.
          <br />
          <span className="text-xs italic">To export CSV from Excel: File → Save As → Choose CSV format</span>
        </p>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleSampleExcelUpload}
              className="hidden"
              id="excel-upload"
            />
            <label
              htmlFor="excel-upload"
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
            >
              <FiUpload className="w-4 h-4 mr-2" />
              Upload CSV/Excel File
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="has-header"
              checked={hasHeaderRow}
              onChange={(e) => setHasHeaderRow(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="has-header" className="text-sm text-gray-700">
              My file has a header row with column names (uncheck if first row contains student data)
            </label>
          </div>
        </div>
      </div>

      {/* Upload Message */}
      {uploadMessage && (
        <div className={`p-3 rounded-lg ${uploadMessage.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          <div className="flex items-center">
            {uploadMessage.includes('Error') ? (
              <FiAlertCircle className="w-4 h-4 mr-2" />
            ) : (
              <FiCheck className="w-4 h-4 mr-2" />
            )}
            <span className="text-sm">{uploadMessage}</span>
          </div>
        </div>
      )}

      {/* Excel Preview */}
      {sampleExcelData && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">File Preview</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Column</th>
                  {(hasHeaderRow ? sampleExcelData[0] : sampleExcelData[0].map((_, index) => `Column ${index + 1}`)).map((header, index) => (
                    <th key={index} className="px-3 py-2 text-left text-sm font-medium text-gray-700">
                      {String.fromCharCode(65 + index)} {hasHeaderRow ? `(${header})` : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(hasHeaderRow ? sampleExcelData.slice(1, 4) : sampleExcelData.slice(0, 3)).map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-t">
                    <td className="px-3 py-2 text-sm text-gray-600">
                      Row {hasHeaderRow ? rowIndex + 2 : rowIndex + 1}
                    </td>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="px-3 py-2 text-sm text-gray-900">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Auto-Generation Button */}
      {canAutoGenerate && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-green-900 mb-1">Auto-Populate from CSV Structure</h4>
              <p className="text-sm text-green-700">
                Detected CSV structure with Assessment/Grand Total rows. 
                Will use Assessment Total for maximum marks (or Grand Total if Assessment Total not found).
                Click to automatically populate the component fields below. You can edit them before saving.
              </p>
            </div>
            <button
              type="button"
              onClick={generateComponentsFromCSV}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 ml-4"
            >
              <FiPlus className="w-4 h-4 mr-2" />
              Auto-Populate Fields
            </button>
          </div>
        </div>
      )}

      {/* Total Rows Preview */}
      {canAutoGenerate && sampleExcelData && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-3">Detected Assessment Structure</h4>
          <div className="space-y-3">
            {(() => {
              let headerRow = null;
              let assessmentTotalRow = null;
              let grandTotalRow = null;
              let assessmentTotalRowIndex = -1;
              let grandTotalRowIndex = -1;

              // Find the relevant rows
              for (let i = 0; i < sampleExcelData.length; i++) {
                const row = sampleExcelData[i];
                if (row[0] && row[0].includes('Assessment Total')) {
                  assessmentTotalRow = row;
                  assessmentTotalRowIndex = i;
                  headerRow = i > 0 ? sampleExcelData[i - 1] : null;
                } else if (row[0] && row[0].includes('Grand Total')) {
                  grandTotalRow = row;
                  grandTotalRowIndex = i;
                  if (!headerRow && i > 0) {
                    headerRow = sampleExcelData[i - 1];
                  }
                }
              }

              const usingGrandTotal = grandTotalRow !== null;
              const totalRowForMaxMarks = grandTotalRow || assessmentTotalRow;

              return (
                <div className="space-y-4">
                  {/* Header Row */}
                  {headerRow && (
                    <div>
                      <h5 className="text-sm font-medium text-blue-800 mb-2">Column Headers:</h5>
                      <div className="bg-white p-2 rounded border">
                        <div className="flex flex-wrap gap-2">
                          {headerRow.slice(1).map((header, index) => (
                            <span key={index} className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {String.fromCharCode(66 + index)}: {header}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Assessment Total Row */}
                  {assessmentTotalRow && (
                    <div>
                      <h5 className="text-sm font-medium text-blue-800 mb-2">
                        Assessment Total (Row {assessmentTotalRowIndex + 1}):
                      </h5>
                      <div className="bg-white p-2 rounded border">
                        <div className="flex flex-wrap gap-2">
                          {assessmentTotalRow.slice(1).map((total, index) => (
                            <span key={index} className="text-xs bg-orange-100 px-2 py-1 rounded">
                              {String.fromCharCode(66 + index)}: {total}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grand Total Row */}
                  {grandTotalRow && (
                    <div>
                      <h5 className="text-sm font-medium text-blue-800 mb-2">
                        Grand Total (Row {grandTotalRowIndex + 1}):
                      </h5>
                      <div className="bg-white p-2 rounded border">
                        <div className="flex flex-wrap gap-2">
                          {grandTotalRow.slice(1).map((total, index) => (
                            <span key={index} className={`text-xs px-2 py-1 rounded ${
                              usingGrandTotal ? 'bg-green-100 text-green-800' : 'bg-gray-100'
                            }`}>
                              {String.fromCharCode(66 + index)}: {total}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Usage Indicator */}
                  <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                    <div className="flex items-center">
                      <FiAlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
                      <span className="text-sm text-yellow-800">
                        <strong>Will use {usingGrandTotal ? 'Grand Total' : 'Assessment Total'} values for Maximum Marks:</strong>
                        {' '}
                        {totalRowForMaxMarks && totalRowForMaxMarks.slice(1).map((total, index) => (
                          <span key={index} className="font-mono">
                            {String.fromCharCode(66 + index)}:{total}
                            {index < totalRowForMaxMarks.slice(1).length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Category and Component Configuration */}
      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Assessment Column Mapping</h3>
        <p className="text-sm text-gray-600 mb-4">
          Define how your Excel columns map to assessment components. Each component represents a column in your Excel file.
        </p>
      </div>

      {categories.some(cat => cat.displayName.includes('Auto-populated')) && (
        <div className="bg-blue-50 p-3 rounded-lg mb-4">
          <div className="flex items-start">
            <FiAlertCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5" />
            <div className="text-sm text-blue-800">
              <span className="font-medium">Auto-populated fields are editable!</span> Review the component names, 
              column mappings, and max marks below. Make any necessary changes before saving the template.
            </div>
          </div>
        </div>
      )}
      
      <div className="space-y-4">
        {console.log('Rendering categories:', categories)}
        {categories.length === 0 && (
          <div className="text-gray-500 text-center py-4">
            No assessment components defined yet. Add a category to get started.
          </div>
        )}
        {categories.map((category) => (
          <div key={category.id} className={`border rounded-lg p-4 ${
            category.displayName.includes('Auto-populated') 
              ? 'border-green-300 bg-green-50/30' 
              : 'border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => toggleCategoryCollapse(category.id)}
                  className="p-1 text-gray-600 hover:bg-gray-100 rounded-md"
                  title={collapsedCategories.has(category.id) ? 'Expand category' : 'Collapse category'}
                >
                  {collapsedCategories.has(category.id) ? (
                    <FiChevronDown className="w-4 h-4" />
                  ) : (
                    <FiChevronUp className="w-4 h-4" />
                  )}
                </button>
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category Name
                    </label>
                    <input
                      type="text"
                      value={category.displayName}
                      onChange={(e) => updateCategory(category.id, 'displayName', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category Key
                    </label>
                    <input
                      type="text"
                      value={category.name}
                      onChange={(e) => updateCategory(category.id, 'name', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeCategory(category.id)}
                className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-md"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Collapsed Summary */}
            {collapsedCategories.has(category.id) && (
              <div className="text-sm text-gray-600 py-2 border-t">
                <span className="font-medium">{category.components.length} components</span>
                {category.components.length > 0 && (
                  <span className="ml-2">
                    ({category.components.map(c => c.displayName).join(', ')})
                  </span>
                )}
              </div>
            )}

            {/* Components */}
            {!collapsedCategories.has(category.id) && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h5 className="text-sm font-medium text-gray-700">Assessment Components</h5>
                  <button
                    type="button"
                    onClick={() => addComponent(category.id)}
                    className="flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                  >
                    <FiPlus className="w-3 h-3 mr-1" />
                    Add Component
                  </button>
                </div>

                {category.components.map((component) => (
                <div key={component.id} className="bg-gray-50 p-3 rounded-md">
                  <div className="grid grid-cols-6 gap-3 items-end">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Component Name
                      </label>
                      <input
                        type="text"
                        value={component.displayName}
                        onChange={(e) => updateComponent(category.id, component.id, 'displayName', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Excel Column
                      </label>
                      <select
                        value={component.columnIndex}
                        onChange={(e) => updateComponent(category.id, component.id, 'columnIndex', parseInt(e.target.value))}
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                      >
                        {Array.from({ length: 20 }, (_, i) => i).map(num => (
                          <option key={num} value={num}>
                            {String.fromCharCode(65 + num)} (Column {num + 1})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Max Marks
                      </label>
                      <input
                        type="number"
                        value={component.maxMarks}
                        onChange={(e) => updateComponent(category.id, component.id, 'maxMarks', parseInt(e.target.value))}
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Grand Total
                      </label>
                      <input
                        type="number"
                        value={component.grandTotal || ''}
                        onChange={(e) => updateComponent(category.id, component.id, 'grandTotal', parseInt(e.target.value))}
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                        placeholder="Auto-detected"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={component.description}
                        onChange={(e) => updateComponent(category.id, component.id, 'description', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => removeComponent(category.id, component.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded-md"
                      >
                        <FiTrash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* YAML Preview */}
      {showPreview && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Generated YAML</h4>
          <pre className="text-sm text-gray-800 bg-white p-3 rounded border overflow-x-auto">
            {generateYAML()}
          </pre>
        </div>
      )}

      {/* Validation Messages */}
      <div className="bg-green-50 p-3 rounded-lg">
        <div className="flex items-center">
          <FiCheck className="w-4 h-4 text-green-600 mr-2" />
          <span className="text-sm text-green-800">
            Column mapping configured successfully. This will be automatically added to your template.
          </span>
        </div>
      </div>
    </div>
  );
};

export default ExcelColumnMapper;